import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import type {RecordChanges, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';

const PROTOTYPE_KEY = '__proto__';
/** The key every fill carries and no other record does. */
const FILL_TYPE_KEY = 'fillType';

export const isSnapshotRecord = (value: unknown): value is SnapshotRecord => {
  return isPlainObject(value);
};

/**
 * A record that changes whole or not at all: a fill. Each fill type stores different keys, so merging two writers' fills key by key could leave
 * a fill holding keys of two types.
 */
const isWholeValueRecord = (value: SnapshotRecord): boolean => {
  return FILL_TYPE_KEY in value;
};

/** A record whose changes are merged key by key. */
const isMergeableRecord = (value: unknown): value is SnapshotRecord => {
  return isSnapshotRecord(value) && !isWholeValueRecord(value);
};

export const hasRecordChanges = (changes: RecordChanges): boolean => {
  return Object.keys(changes.set).length > 0 || changes.unset.length > 0;
};

/**
 * Leaf-level difference that turns `before` into `after`. Nested plain objects are compared key
 * by key so two writers touching different leaves of one record never overwrite each other; a fill
 * is one leaf. An `undefined` value counts as an absent key, matching how jsondiffpatch snapshots
 * treat it.
 */
export const diffRecords = (before: SnapshotRecord, after: SnapshotRecord): RecordChanges => {
  const changes: RecordChanges = {set: {}, unset: []};

  for (const [key, afterValue] of Object.entries(after)) {
    if (afterValue === undefined) {
      continue;
    }
    const beforeValue = before[key];
    if (isMergeableRecord(beforeValue) && isMergeableRecord(afterValue)) {
      const nestedChanges = diffRecords(beforeValue, afterValue);
      if (Object.keys(nestedChanges.set).length > 0) {
        changes.set[key] = nestedChanges.set;
      }
      for (const nestedPath of nestedChanges.unset) {
        changes.unset.push([key, ...nestedPath]);
      }
    } else if (!isEqual(beforeValue, afterValue)) {
      changes.set[key] = afterValue;
    }
  }

  for (const [key, beforeValue] of Object.entries(before)) {
    if (beforeValue !== undefined && after[key] === undefined) {
      changes.unset.push([key]);
    }
  }

  return changes;
};

/** Returns a new record with `changes` applied; `record` is never mutated. */
export const applyRecordChanges = (record: SnapshotRecord, changes: RecordChanges): SnapshotRecord => {
  let result = mergeSetValues(record, changes.set);
  for (const path of changes.unset) {
    result = removeKeyPath(result, path);
  }
  return result;
};

const mergeSetValues = (record: SnapshotRecord, set: SnapshotRecord): SnapshotRecord => {
  const result: SnapshotRecord = {...record};
  for (const [key, value] of Object.entries(set)) {
    // Ops arrive from other people: assigning this key would swap the record's prototype instead of setting a value.
    if (key === PROTOTYPE_KEY) {
      continue;
    }
    const existingValue = result[key];
    result[key] = isMergeableRecord(existingValue) && isMergeableRecord(value) ? mergeSetValues(existingValue, value) : value;
  }
  return result;
};

const removeKeyPath = (record: SnapshotRecord, path: string[]): SnapshotRecord => {
  const [key, ...nestedPath] = path;
  if (key === undefined || !Object.hasOwn(record, key)) {
    return record;
  }
  if (nestedPath.length === 0) {
    const {[key]: _removed, ...rest} = record;
    return rest;
  }
  const nestedRecord = record[key];
  if (!isSnapshotRecord(nestedRecord)) {
    return record;
  }
  return {...record, [key]: removeKeyPath(nestedRecord, nestedPath)};
};
